import React, { useEffect, useState } from "react";
import {
    Container,
    Box,
    Stack,
    Typography,
    Card,
    CardContent,
    TextField,
    CircularProgress,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
    Switch,
    FormControlLabel
} from "@mui/material";
import { useParams } from "react-router-dom";
import {
    ConfigContext,
    useForm,
    http,
    PDFViewer
} from "../m34n-ui/src/lib.js";
import { debounce } from "lodash";
import { parse } from "date-fns";

const saveField = debounce(async (data, uri) => {
    await http.put(data, uri);
}, 500);

const onLoad = async ({ values }) => {
    const product = await http.get({}, `${values.config.uri.base}/product/${values._id}`);
    const emailTemplates = await http.get({
        pageSize: 100
    }, `${values.config.uri.base}/email/template`);
    return {
        ...values,
        ...product,
        emailTemplates: emailTemplates.rows,
        loading: false,
    };
};

const uploadFile = async (file, productId, field, config) => {
    const formData = new FormData();
    formData.append(field, file);

    const response = await http.post(formData, `${config.uri.base}/product/${productId}/${field}`, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'x-app-id': process.env.REACT_APP_APPID,
        }
    });
    return response[`${field}URL`];
};

const uploadThumbnail = async (file, productId, config) => {
    const formData = new FormData();
    formData.append('thumbnail', file);

    const response = await http.post(formData, `${config.uri.base}/product/${productId}/thumbnail`, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'x-app-id': process.env.REACT_APP_APPID,
        }
    });
    return response.thumbnailURL;
};

const uploadHeader = async (file, productId, config) => {
    const formData = new FormData();
    formData.append('header', file);

    const response = await http.post(formData, `${config.uri.base}/product/${productId}/header`, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'x-app-id': process.env.REACT_APP_APPID,
        }
    });
    return response.headerURL;
};

export default function ProductDetail() {
    const params = useParams();
    const { config } = React.useContext(ConfigContext);
    const {
        state,
        onChange,
    } = useForm({
        debug: true,
        initialState: {
            config,
            _id: params.productId,
            name: '',
            description: '',
            emailTemplateId: null,
            emailTemplates: [],
            thumbnailURL: '',
            headerURL: '',
            bookCoverURL: '',
            bookInteriorURL: '',
            registrationTypes: [],
            published: false, // Added published state
            createdAt: null,
            createdBy: '',
            updatedAt: null,
            updatedBy: '',
            deletedAt: null,
            deletedBy: '',
            deleted: false,
            tenantId: '',
            appId: '',
            loading: true
        },
        onLoad
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newRegistrationType, setNewRegistrationType] = useState({
        name: '',
        type: '',
        paymentLink: '',
    });

    const handleThumbnailChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const thumbnailURL = await uploadThumbnail(file, state.values._id, config);
            onChange(thumbnailURL, 'thumbnailURL');
        }
    };

    const handleHeaderChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const headerURL = await uploadHeader(file, state.values._id, config);
            onChange(headerURL, 'headerURL');
        }
    };

    const handleFileChange = async (event, field) => {
        const file = event.target.files[0];
        if (file) {
            const fileURL = await uploadFile(file, state.values._id, field, config);
            onChange(fileURL, field);
        }
    };

    const handleDelete = async () => {
        await http.delete({}, `${config.uri.base}/product/${state.values._id}`);
        setDeleteDialogOpen(false);
        // Redirect or update UI after  deletion
    };

    const handleRegistrationTypeChange = (event, index) => {
        let { name, value } = event.target;
        const updatedRegistrationTypes = [...state.values.registrationTypes];
        updatedRegistrationTypes[index] = {
            ...updatedRegistrationTypes[index],
            [name]: value
        };
        onChange(updatedRegistrationTypes, 'registrationTypes');
        saveField({ registrationTypes: updatedRegistrationTypes }, `${config.uri.base}/product/${state.values._id}`);
    };

    const handleAddRegistrationType = () => {
        const updatedRegistrationTypes = [...state.values.registrationTypes, newRegistrationType];
        onChange(updatedRegistrationTypes, 'registrationTypes');
        saveField({ registrationTypes: updatedRegistrationTypes }, `${config.uri.base}/product/${state.values._id}`);
        setNewRegistrationType({ name: '', type: '', paymentLink: '' });
        setAddDialogOpen(false);
    };

    const handleRemoveRegistrationType = (index) => {
        const updatedRegistrationTypes = state.values.registrationTypes.filter((_, i) => i !== index);
        onChange(updatedRegistrationTypes, 'registrationTypes');
        saveField({ registrationTypes: updatedRegistrationTypes }, `${config.uri.base}/product/${state.values._id}`);
    };

    const handleEmailTemplateTypeChange = (event, index) => {
        let { name, value } = event.target;
        onChange(value, 'emailTemplateId');
        saveField({ emailTemplateId: value }, `${config.uri.base}/product/${state.values._id}`);
    };

    // Handler for Publish toggle
    const handlePublishToggle = (event) => {
        const newPublished = event.target.checked;
        onChange(newPublished, 'published');
        saveField({ published: newPublished }, `${config.uri.base}/product/${state.values._id}`);
    };

    return (
        <React.Fragment>
            {state.values.loading &&
                <Container>
                    <CircularProgress />
                </Container>
            }
            {!state.values.loading &&
                <Container>
                    <Box sx={{ marginTop: '20px' }}>
                        <Typography variant="h5">Product Details</Typography>
                        <Stack spacing={2} sx={{ marginTop: '20px' }}>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    window.open(
                                        `${config.uri.webapp}/catalog/product/${state.values._id}`,
                                        '_blank'
                                    );
                                }}
                            >
                                View Public Page
                            </Button>
                            <TextField
                                label="Product Name"
                                value={state.values.name}
                                onChange={(e) => {
                                    onChange(e.target.value, 'name');
                                    saveField({ name: e.target.value }, `${config.uri.base}/product/${state.values._id}`);
                                }}
                                fullWidth
                            />
                            <TextField
                                label="Description"
                                value={state.values.description}
                                onChange={(e) => {
                                    onChange(e.target.value, 'description');
                                    saveField({ description: e.target.value }, `${config.uri.base}/product/${state.values._id}`);
                                }}
                                fullWidth
                                multiline
                                rows={4}
                            />
                            {/* Publish to Catalog Toggle */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={state.values.published}
                                        onChange={handlePublishToggle}
                                        color="primary"
                                    />
                                }
                                label="Publish to Catalog"
                            />
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Order Confirmation Email Template</InputLabel>
                                <Select
                                    name="type"
                                    value={state.values.emailTemplateId}
                                    onChange={(e) => handleEmailTemplateTypeChange(e)}
                                >
                                    {state.values.emailTemplates && state.values.emailTemplates.map((template) => (
                                        <MenuItem key={template._id} value={template._id}>{template.subject}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant="h6">Registration Types</Typography>
                            {state.values.registrationTypes.map((type, index) => (
                                <Box key={index} display="flex" alignItems="center">
                                    <Box flexGrow={1}>
                                        <TextField
                                            label="Registration Type Name"
                                            name="name"
                                            value={type.name}
                                            onChange={(e) => handleRegistrationTypeChange(e, index)}
                                            fullWidth
                                            margin="normal"
                                        />
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Type</InputLabel>
                                            <Select
                                                name="type"
                                                value={type.type}
                                                onChange={(e) => handleRegistrationTypeChange(e, index)}
                                            >
                                                <MenuItem value="print">Print</MenuItem>
                                                <MenuItem value="pdf">PDF</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            label="Price"
                                            name="price"
                                            value={type.price}
                                            onChange={(e) => {
                                                handleRegistrationTypeChange(e, index);
                                            }}
                                            fullWidth
                                            margin="normal"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                inputProps: { pattern: "^[0-9]+(\\.[0-9]{1,2})?$" },
                                            }}
                                        />
                                        {type.type === 'print' && (
                                            <TextField
                                                label="Print SKU ID"
                                                name="printSkuId"
                                                value={type.printSkuId}
                                                onChange={(e) => handleRegistrationTypeChange(e, index)}
                                                fullWidth
                                                margin="normal"
                                            />
                                        )}
                                    </Box>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={() => handleRemoveRegistrationType(index)}
                                        sx={{ ml: 2 }}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            ))}
                            <Button
                                variant="contained"
                                onClick={() => setAddDialogOpen(true)}
                                sx={{ mt: 2 }}
                            >
                                Add Registration Type
                            </Button>
                            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
                                <DialogTitle>Add Registration Type</DialogTitle>
                                <DialogContent>
                                    <DialogContentText>
                                        Fill in the details for the new registration type.
                                    </DialogContentText>
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        label="Registration Type Name"
                                        fullWidth
                                        value={newRegistrationType.name}
                                        onChange={(e) => setNewRegistrationType({ ...newRegistrationType, name: e.target.value })}
                                    />
                                    <FormControl fullWidth margin="dense">
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            value={newRegistrationType.type}
                                            onChange={(e) => setNewRegistrationType({ ...newRegistrationType, type: e.target.value })}
                                        >
                                            <MenuItem value="print">Print</MenuItem>
                                            <MenuItem value="pdf">PDF</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        margin="dense"
                                        label="Price"
                                        fullWidth
                                        value={newRegistrationType.price}
                                        onChange={(e) => setNewRegistrationType({ ...newRegistrationType, price: e.target.value })}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                            inputProps: { pattern: "^[0-9]+(\\.[0-9]{1,2})?$" },
                                        }}
                                    />
                                    {newRegistrationType.type === 'print' && (
                                        <TextField
                                            margin="dense"
                                            label="Print SKU ID"
                                            fullWidth
                                            value={newRegistrationType.printSkuId}
                                            onChange={(e) => setNewRegistrationType({ ...newRegistrationType, printSkuId: e.target.value })}
                                        />
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setAddDialogOpen(false)} color="primary">
                                        Cancel
                                    </Button>
                                    <Button onClick={handleAddRegistrationType} color="primary">
                                        Add
                                    </Button>
                                </DialogActions>
                            </Dialog>
                            <Box>
                                <Button
                                    variant="contained"
                                    component="label"
                                >
                                    Upload Book Cover (PDF)
                                    <input
                                        type="file"
                                        hidden
                                        accept="application/pdf"
                                        onChange={(e) => handleFileChange(e, 'bookCoverURL')}
                                    />
                                </Button>
                                {state.values.bookCoverURL && (
                                    <PDFViewer fileURL={state.values.bookCoverURL} style={{ marginTop: '10px' }} />
                                )}
                            </Box>
                            <Box>
                                <Button
                                    variant="contained"
                                    component="label"
                                >
                                    Upload Book Interior (PDF)
                                    <input
                                        type="file"
                                        hidden
                                        accept="application/pdf"
                                        onChange={(e) => handleFileChange(e, 'bookInteriorURL')}
                                    />
                                </Button>
                                {state.values.bookInteriorURL && (
                                    <PDFViewer fileURL={state.values.bookInteriorURL} style={{ marginTop: '10px' }} />
                                )}
                            </Box>
                            <Box>
                                <Button
                                    variant="contained"
                                    component="label"
                                >
                                    Upload Thumbnail
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleThumbnailChange}
                                    />
                                </Button>
                                {state.values.thumbnailURL && (
                                    <Box sx={{ marginTop: '10px' }}>
                                        <img
                                            src={state.values.thumbnailURL}
                                            alt="Thumbnail"
                                            style={{ width: '100%', maxWidth: '300px', height: 'auto' }}
                                        />
                                    </Box>
                                )}
                            </Box>
                            <Box>
                                <Button
                                    variant="contained"
                                    component="label"
                                >
                                    Upload Product Header
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleHeaderChange}
                                    />
                                </Button>
                                {state.values.headerURL && (
                                    <Box sx={{ marginTop: '10px' }}>
                                        <img
                                            src={state.values.headerURL}
                                            alt="Product Header"
                                            style={{ width: '100%', maxWidth: '300px', height: 'auto' }}
                                        />
                                    </Box>
                                )}
                            </Box>

                        </Stack>
                    </Box>
                    <Box sx={{ marginTop: '20px' }}>
                        <Typography variant="h6">Read-Only Fields</Typography>
                        <Card sx={{ marginTop: '10px' }}>
                            <CardContent>
                                <Typography><strong>ID:</strong> {state.values._id}</Typography>
                                <Typography><strong>Created At:</strong> {state.values.createdAt}</Typography>
                                <Typography><strong>Created By:</strong> {state.values.createdBy}</Typography>
                                <Typography><strong>Updated At:</strong> {state.values.updatedAt}</Typography>
                                <Typography><strong>Updated By:</strong> {state.values.updatedBy}</Typography>
                                <Typography><strong>Deleted At:</strong> {state.values.deletedAt}</Typography>
                                <Typography><strong>Deleted By:</strong> {state.values.deletedBy}</Typography>
                                <Typography><strong>Deleted:</strong> {state.values.deleted ? 'Yes' : 'No'}</Typography>
                                <Typography><strong>Tenant ID:</strong> {state.values.tenantId}</Typography>
                                <Typography><strong>App ID:</strong> {state.values.appId}</Typography>
                                <Typography><strong>Published:</strong> {state.values.published ? 'Yes' : 'No'}</Typography> {/* Optional: Display published state */}
                            </CardContent>
                        </Card>
                        <Box sx={{ marginTop: '20px' }}>
                            <Button variant="contained" color="error" onClick={() => setDeleteDialogOpen(true)}>
                                Delete Product
                            </Button>
                            <Dialog
                                open={deleteDialogOpen}
                                onClose={() => setDeleteDialogOpen(false)}
                            >
                                <DialogTitle>Delete Product</DialogTitle>
                                <DialogContent>
                                    <DialogContentText>
                                        Are you sure you want to delete this product?
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                                        Cancel
                                    </Button>
                                    <Button onClick={handleDelete} color="error">
                                        Delete
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Box>
                    </Box>
                </Container>
            }
        </React.Fragment>
    );
}

